import { useState, useEffect, useCallback } from 'react';
import { STATUS_CONFIG, type Shipment } from '../../../mocks/shipments';
import { fetchAllShipments } from '../../../lib/shipmentsService';

export default function AdminOverviewTab() {
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllShipments();
      setShipments(data);
    } catch {
      setError('Unable to load shipments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="w-10 h-10 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-teal-600 text-sm">Loading overview...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <i className="ri-error-warning-line text-4xl text-red-400" />
        <p className="text-red-300 text-sm">{error}</p>
        <button
          onClick={load}
          className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
          style={{ background: 'var(--primary)' }}
        >
          <i className="ri-refresh-line mr-1.5" />Retry
        </button>
      </div>
    );
  }

  const totalRevenue = shipments.reduce((acc, s) => acc + s.value, 0);
  const delivered = shipments.filter((s) => s.status === 'delivered');
  const inTransit = shipments.filter((s) => s.status === 'in_transit');
  const onHold = shipments.filter((s) => s.status === 'hold' || s.status === 'on_hold');
  const outForDelivery = shipments.filter((s) => s.status === 'out_for_delivery');
  const express = shipments.filter((s) => s.service === 'Express');

  const STAT_CARDS = [
    {
      label: 'Total Shipments',
      value: shipments.length.toString(),
      icon: 'ri-box-3-line',
      color: 'var(--primary)',
      badge: null,
      sub: `${delivered.length} delivered`,
    },
    {
      label: 'In Transit',
      value: inTransit.length.toString(),
      icon: 'ri-truck-line',
      color: '#f97316',
      badge: null,
      sub: 'Currently moving',
    },
    {
      label: 'On Hold',
      value: onHold.length.toString(),
      icon: 'ri-pause-circle-line',
      color: '#ef4444',
      badge: onHold.length > 0 ? 'Needs attention' : null,
      sub: 'Require review',
    },
    {
      label: 'Delivered',
      value: delivered.length.toString(),
      icon: 'ri-checkbox-circle-line',
      color: '#22c55e',
      badge: null,
      sub: 'Successfully completed',
    },
  ];

  const highestValue = shipments.length > 0 ? Math.max(...shipments.map((s) => s.value)) : 0;
  const avgValue = shipments.length > 0 ? Math.round(totalRevenue / shipments.length) : 0;

  return (
    <div className="space-y-8">
      {/* Hold alert */}
      {!alertDismissed && onHold.length > 0 && (
        <div className="flex items-center justify-between bg-red-900/20 border border-red-500/30 rounded-xl px-5 py-4">
          <div className="flex items-center gap-3">
            <i className="ri-alarm-warning-line text-red-400 text-xl" />
            <div>
              <div className="text-red-300 font-semibold text-sm">
                {onHold.length} shipment{onHold.length > 1 ? 's' : ''} currently on hold
              </div>
              <div className="text-red-500 text-xs mt-0.5">
                {onHold.map((s) => s.id).join(', ')} — requires attention
              </div>
            </div>
          </div>
          <button onClick={() => setAlertDismissed(true)} className="text-red-500 hover:text-red-300 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      )}

      {/* Revenue Summary Banner */}
      <div
        className="rounded-2xl p-6 flex flex-wrap items-center justify-between gap-6 border border-teal-500/20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, #0a3333 60%, #0d2020 100%)' }}
      >
        <div className="absolute right-0 top-0 w-64 h-full opacity-5 pointer-events-none">
          <i className="ri-money-dollar-circle-line text-[220px] text-teal-400 absolute -right-10 -top-8" />
        </div>

        <div className="relative z-10">
          <div className="text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1">Total Declared Revenue</div>
          <div className="font-heading font-bold text-4xl text-white">
            ${totalRevenue.toLocaleString()}
          </div>
          <div className="text-teal-500 text-sm mt-1">
            Across {shipments.length} shipments · avg ${avgValue.toLocaleString()} per shipment
          </div>
        </div>

        <div className="relative z-10 flex gap-6 flex-wrap">
          {[
            {
              label: 'Highest Value',
              value: `$${highestValue.toLocaleString()}`,
              icon: 'ri-arrow-up-circle-line',
              color: '#22c55e',
            },
            {
              label: 'Express Shipments',
              value: express.length.toString(),
              icon: 'ri-flashlight-line',
              color: '#f59e0b',
            },
            {
              label: 'Out for Delivery',
              value: outForDelivery.length.toString(),
              icon: 'ri-e-bike-2-line',
              color: 'var(--primary)',
            },
          ].map(({ label, value, icon, color }) => (
            <div key={label} className="text-center">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-1.5"
                style={{ background: `color-mix(in srgb, ${color} 20%, transparent)` }}
              >
                <i className={`${icon} text-sm`} style={{ color }} />
              </div>
              <div className="font-bold text-white text-lg">{value}</div>
              <div className="text-teal-600 text-xs">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {STAT_CARDS.map(({ label, value, icon, color, badge, sub }) => (
          <div key={label} className="bg-[#0d2020] border border-white/5 rounded-2xl p-6 relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `color-mix(in srgb, ${color} 15%, transparent)` }}
              >
                <i className={`${icon} text-lg`} style={{ color }} />
              </div>
              {badge && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20 whitespace-nowrap">
                  {badge}
                </span>
              )}
            </div>
            <div className="font-heading font-bold text-3xl text-white mb-0.5">{value}</div>
            <div className="text-teal-600 text-sm">{label}</div>
            <div className="text-teal-800 text-xs mt-1">{sub}</div>
          </div>
        ))}
      </div>

      {/* Status distribution bar */}
      <div className="bg-[#0d2020] border border-white/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-semibold text-teal-300 text-sm uppercase tracking-wider">Shipment Distribution</h3>
          <span className="text-teal-600 text-xs">{shipments.length} total</span>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden gap-0.5 mb-4">
          {[
            { status: 'delivered', color: '#16a34a' },
            { status: 'out_for_delivery', color: '#22c55e' },
            { status: 'in_transit', color: 'var(--primary)' },
            { status: 'picked_up', color: '#f59e0b' },
            { status: 'hold', color: '#ef4444' },
          ].map(({ status, color }) => {
            const count = shipments.filter((s) => s.status === status).length;
            const pct = shipments.length > 0 ? (count / shipments.length) * 100 : 0;
            return pct > 0 ? (
              <div key={status} style={{ width: `${pct}%`, background: color }} className="rounded-sm" title={`${STATUS_CONFIG[status as keyof typeof STATUS_CONFIG].label}: ${count}`} />
            ) : null;
          })}
        </div>
        <div className="flex flex-wrap gap-4">
          {(Object.entries(STATUS_CONFIG) as [keyof typeof STATUS_CONFIG, typeof STATUS_CONFIG[keyof typeof STATUS_CONFIG]][]).map(([key, cfg]) => {
            const count = shipments.filter((s) => s.status === key).length;
            return (
              <div key={key} className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color}`}>
                  <i className={cfg.icon} />
                  {cfg.label}
                </span>
                <span className="text-teal-500 text-xs font-bold">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent shipments */}
      <div>
        <h3 className="font-heading font-semibold text-teal-300 text-sm uppercase tracking-wider mb-4">
          Recent Shipments
        </h3>
        {shipments.length === 0 ? (
          <div className="text-center py-12 text-teal-700">
            <i className="ri-inbox-line text-3xl mb-3 block" />
            <p className="text-sm">No shipments yet. Create one from the Create Shipment tab.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {shipments.slice(0, 5).map((s) => {
              const cfg = STATUS_CONFIG[s.status];
              return (
                <div key={s.id} className="flex items-center justify-between bg-[#0d2020] border border-white/5 rounded-xl px-5 py-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div>
                      <span className="font-mono text-xs text-white font-bold">{s.id}</span>
                      <div className="text-teal-600 text-xs mt-0.5">
                        {s.origin.city} → {s.destination.city} · {s.service}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-white text-xs font-bold">${s.value.toLocaleString()}</div>
                      <div className="text-teal-700 text-xs">{s.weight} kg</div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.color}`}>
                      {s.status === 'hold' && <span className="w-1.5 h-1.5 rounded-full bg-current animate-ping" />}
                      <i className={cfg.icon} />
                      {cfg.label}
                    </span>
                    <span className="text-teal-600 text-xs">{s.estimatedDelivery}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}